import React, { useState, useEffect } from 'react';
import { textoFaixa } from '../../utils/comissaoPorQtd';
import { Plus, Edit, Trash2, Eye, Star, PowerOff, Power } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { comissoesService } from '../../services/comissoesService';
import faixasDefesaService from '../../services/faixasDefesaService';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/ui/Toast';
import NovaComissaoModal from '../../components/modals/NovaComissaoModal';
import EditarComissaoModal from '../../components/modals/EditarComissaoModal';
import FaixasDefesaEditor from '../../components/comissoes/FaixasDefesaEditor';

const ComissoesLista = () => {
  const { organizadorAtivo } = useOrganizador();
  const { toasts, success, error, warning, hideToast } = useToast();
  const [comissoes, setComissoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNovaModal, setShowNovaModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [comissaoSelecionada, setComissaoSelecionada] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('ativas');
  const [selecionadas, setSelecionadas] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [faixasDefesa, setFaixasDefesa] = useState([]);
  const [salvandoFaixas, setSalvandoFaixas] = useState(false);

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarComissoes();
      carregarFaixasDefesa();
    }
  }, [organizadorAtivo?.id]);

  const carregarFaixasDefesa = async () => {
    if (!organizadorAtivo?.id) return;
    try {
      const data = await faixasDefesaService.listarPadrao(organizadorAtivo.id);
      setFaixasDefesa(data.length ? data : []);
    } catch (err) {
      console.error('Erro ao carregar faixas de defesa:', err);
    }
  };

  const handleSalvarFaixasDefesa = async () => {
    const faixasInvalidas = faixasDefesa.some(f => f.valor_fixo === '' || f.valor_fixo === null || f.valor_min === '' || f.valor_min === null);
    if (faixasInvalidas) {
      error('Preencha o valor "De" e a "Defesa fixa" em todas as faixas.');
      return;
    }
    try {
      setSalvandoFaixas(true);
      const payload = faixasDefesa.map((f, i) => ({
        valor_min: parseFloat(f.valor_min) || 0,
        valor_max: f.valor_max === '' || f.valor_max === null ? null : parseFloat(f.valor_max),
        valor_fixo: parseFloat(f.valor_fixo) || 0,
        ordem: i,
      }));
      const salvas = await faixasDefesaService.salvarPadrao(organizadorAtivo.id, payload);
      setFaixasDefesa(salvas);
      success('Faixas de defesa salvas com sucesso!');
    } catch (err) {
      console.error('Erro ao salvar faixas de defesa:', err);
      error('Erro ao salvar faixas de defesa');
    } finally {
      setSalvandoFaixas(false);
    }
  };

  const carregarComissoes = async () => {
    if (!organizadorAtivo?.id) return;
    try {
      setLoading(true);
      const response = await comissoesService.listar(organizadorAtivo.id);
      setComissoes(response.data);
    } catch (error) {
      console.error('Erro ao carregar comissões:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExcluir = async (id, nome) => {
    try {
      const response = await comissoesService.verificarEmUso(organizadorAtivo.id, id);
      const { em_uso, qtd_lotes } = response.data || response;
      if (em_uso) {
        error(`Não é possível excluir esta comissão pois ela já foi utilizada em ${qtd_lotes} lote(s) faturado(s).`);
        return;
      }
    } catch (err) {
      console.error('Erro ao verificar uso da comissão:', err);
    }
    setItemToDelete({ id, nome });
    setShowConfirmDelete(true);
  };

  const confirmarExclusao = async () => {
    if (!itemToDelete) return;
    
    try {
      await comissoesService.excluir(organizadorAtivo.id, itemToDelete.id);
      success('Comissão excluída com sucesso!');
      await carregarComissoes();
    } catch (err) {
      console.error('Erro ao excluir comissão:', err);
      if (err.response?.status === 409) {
        error('Não é possível excluir esta comissão pois ela está sendo utilizada em leilões.');
      } else {
        error('Erro ao excluir comissão');
      }
    }
    setItemToDelete(null);
    setShowConfirmDelete(false);
  };

  const handleEditar = async (comissao) => {
    try {
      const response = await comissoesService.verificarEmUso(organizadorAtivo.id, comissao.id);
      const { em_uso, qtd_lotes } = response.data || response;
      if (em_uso) {
        error(`Não é possível editar esta comissão pois ela já foi utilizada em ${qtd_lotes} lote(s) faturado(s). Alterar os valores afetaria o faturamento e as parcelas.`);
        return;
      }
    } catch (err) {
      console.error('Erro ao verificar uso da comissão:', err);
    }
    setComissaoSelecionada(comissao);
    setShowEditarModal(true);
  };

  const handleDetalhes = (comissao) => {
    setComissaoSelecionada(comissao);
    setShowDetalhesModal(true);
  };

  const handleMarcarPadrao = async (comissao) => {
    try {
      await comissoesService.marcarPadrao(organizadorAtivo.id, comissao.id);
      success(`Comissão "${comissao.nome}" marcada como padrão!`);
      await carregarComissoes();
    } catch (err) {
      console.error('Erro ao marcar comissão como padrão:', err);
      error('Erro ao marcar comissão como padrão');
    }
  };

  const comissoesFiltradas = comissoes.filter(comissao => {
    if (filtroTipo !== 'todos' && comissao.tipo_comissao !== filtroTipo) return false;
    if (filtroStatus === 'ativas' && comissao.ativo === false) return false;
    if (filtroStatus === 'inativas' && comissao.ativo !== false) return false;
    return true;
  });

  const toggleSelecionada = (id) => {
    setSelecionadas(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleTodas = () => {
    if (selecionadas.length === comissoesFiltradas.length) {
      setSelecionadas([]);
    } else {
      setSelecionadas(comissoesFiltradas.map(c => c.id));
    }
  };

  const handleInativarSelecionadas = async () => {
    if (selecionadas.length === 0) return;
    try {
      await comissoesService.inativarLote(organizadorAtivo.id, selecionadas, false);
      success(`${selecionadas.length} comissão(ões) inativada(s)!`);
      setSelecionadas([]);
      await carregarComissoes();
    } catch (err) {
      error('Erro ao inativar comissões');
    }
  };

  const handleAtivarSelecionadas = async () => {
    if (selecionadas.length === 0) return;
    try {
      await comissoesService.inativarLote(organizadorAtivo.id, selecionadas, true);
      success(`${selecionadas.length} comissão(ões) ativada(s)!`);
      setSelecionadas([]);
      await carregarComissoes();
    } catch (err) {
      error('Erro ao ativar comissões');
    }
  };

  const formatarPercentual = (valor) => {
    return valor ? `${valor}%` : '-';
  };

  // Comissão por quantidade: mostra um resumo das faixas no lugar do % único de venda
  const resumoVenda = (comissao) => comissao.tipo_comissao === 'por_qtd'
    ? 'por qtd'
    : formatarPercentual(comissao.comissao_venda);

  const renderBadgeTipo = (tipo) => {
    if (tipo === 'por_qtd') {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">🐂 Por Qtd</span>;
    }
    if (tipo === 'socios') {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">👥 Sócios</span>;
    }
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">📊 Padrão</span>;
  };

  return (
    <>
      <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
      
      <div>
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Comissões</h2>
        <button
          onClick={() => setShowNovaModal(true)}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Nova </span>Comissão
        </button>
      </div>

      {!organizadorAtivo?.usa_comissao_socios && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>💡 Dica:</strong> Para usar comissões diferenciadas por sócios, 
                ative o produto "Comissão por Sócios" nas configurações do organizador.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Lista de Comissões</h3>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {selecionadas.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-500">{selecionadas.length} sel.</span>
                {filtroStatus === 'ativas' ? (
                  <button onClick={handleInativarSelecionadas}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-red-200 transition-colors">
                    <PowerOff className="w-3.5 h-3.5" /> Inativar
                  </button>
                ) : (
                  <button onClick={handleAtivarSelecionadas}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-green-200 transition-colors">
                    <Power className="w-3.5 h-3.5" /> Ativar
                  </button>
                )}
              </div>
            )}
            <select value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setSelecionadas([]); }}
              className="input-rematei text-sm w-28 sm:w-36">
              <option value="ativas">Ativas</option>
              <option value="inativas">Inativas</option>
              <option value="todos">Todas</option>
            </select>
            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
              className="input-rematei text-sm w-28 sm:w-36">
              <option value="todos">Todos tipos</option>
              <option value="padrao">Padrão</option>
              {organizadorAtivo?.usa_comissao_socios && <option value="socios">Sócios</option>}
              {organizadorAtivo?.usa_comissao_por_qtd && <option value="por_qtd">Por Quantidade</option>}
            </select>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                {comissoesFiltradas.map((comissao) => (
                  <div key={comissao.id} className={`p-4 ${comissao.is_padrao ? 'bg-yellow-50' : 'bg-white'} ${comissao.ativo === false ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-gray-900 text-sm truncate">{comissao.nome}</span>
                          {comissao.is_padrao && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex-shrink-0">
                              ⭐ Padrão
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {renderBadgeTipo(comissao.tipo_comissao)}
                          <span className="text-xs text-gray-500">
                            C: {formatarPercentual(comissao.comissao_compra)} · V: {resumoVenda(comissao)} · D: {formatarPercentual(comissao.comissao_defesa)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <input type="checkbox" checked={selecionadas.includes(comissao.id)} onChange={() => toggleSelecionada(comissao.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                        <button onClick={() => handleMarcarPadrao(comissao)}
                          className={`p-1.5 rounded ${comissao.is_padrao ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`}>
                          <Star className={`w-4 h-4 ${comissao.is_padrao ? 'fill-current' : ''}`} />
                        </button>
                        <button onClick={() => handleDetalhes(comissao)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEditar(comissao)} className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleExcluir(comissao.id, comissao.nome)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <input type="checkbox"
                          checked={selecionadas.length === comissoesFiltradas.length && comissoesFiltradas.length > 0}
                          onChange={toggleTodas} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comprador</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Defesa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {comissoesFiltradas.map((comissao) => (
                      <tr key={comissao.id} className={`hover:bg-gray-50 ${comissao.is_padrao ? 'bg-yellow-50' : ''} ${comissao.ativo === false ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-4">
                          <input type="checkbox" checked={selecionadas.includes(comissao.id)} onChange={() => toggleSelecionada(comissao.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {comissao.nome}
                            {comissao.is_padrao && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">⭐ Padrão</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderBadgeTipo(comissao.tipo_comissao)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatarPercentual(comissao.comissao_compra)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{resumoVenda(comissao)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatarPercentual(comissao.comissao_defesa)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button onClick={() => handleMarcarPadrao(comissao)}
                            className={`${comissao.is_padrao ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`}
                            title={comissao.is_padrao ? 'Comissão padrão' : 'Marcar como padrão'}>
                            <Star className={`w-4 h-4 ${comissao.is_padrao ? 'fill-current' : ''}`} />
                          </button>
                          <button onClick={() => handleDetalhes(comissao)} className="text-blue-600 hover:text-blue-900"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => handleEditar(comissao)} className="text-yellow-600 hover:text-yellow-900"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleExcluir(comissao.id, comissao.nome)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!loading && comissoesFiltradas.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma comissão encontrada
            </div>
          )}
        </div>
      </div>

      {organizadorAtivo?.fluxo_leilao === 'comissao_cavalo' && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">🐴 Defesa por Faixa de Valor (Leilões Cavalo)</h3>
            <p className="text-sm text-gray-500 mt-1">
              No fluxo Cavalo, a defesa não é percentual: é um valor fixo conforme a faixa de valor da venda.
              Estas são as faixas padrão do organizador — cada leilão pode ter suas próprias faixas (configurável no cadastro do leilão).
            </p>
          </div>
          <div className="p-4 sm:p-6">
            <FaixasDefesaEditor faixas={faixasDefesa} onChange={setFaixasDefesa} />
            <div className="flex justify-end mt-4">
              <button onClick={handleSalvarFaixasDefesa} disabled={salvandoFaixas} className="btn-primary">
                {salvandoFaixas ? 'Salvando...' : 'Salvar Faixas'}
              </button>
            </div>
          </div>
        </div>
      )}

      <NovaComissaoModal
        isOpen={showNovaModal}
        onClose={() => setShowNovaModal(false)}
        onSave={async (data) => {
          try {
            await comissoesService.criar(organizadorAtivo.id, data);
            setShowNovaModal(false);
            await carregarComissoes();
          } catch (error) {
            console.error('Erro ao criar comissão:', error);
          }
        }}
      />

      <EditarComissaoModal
        isOpen={showEditarModal}
        onClose={() => setShowEditarModal(false)}
        comissao={comissaoSelecionada}
        onSave={async (data) => {
          try {
            await comissoesService.atualizar(organizadorAtivo.id, comissaoSelecionada.id, data);
            setShowEditarModal(false);
            success('Comissão atualizada com sucesso!');
            await carregarComissoes();
          } catch (err) {
            console.error('Erro ao atualizar comissão:', err);
            if (err.response?.status === 409) {
              error('Não é possível editar esta comissão pois ela já foi utilizada em lotes faturados. Alterar os valores afetaria o faturamento e as parcelas.');
            } else {
              error('Erro ao atualizar comissão');
            }
          }
        }}
      />

      {showDetalhesModal && comissaoSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl p-5 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-semibold">Detalhes da Comissão</h3>
              <button onClick={() => setShowDetalhesModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
              <div><strong>Nome:</strong> {comissaoSelecionada.nome}</div>
              <div className="flex items-center gap-2"><strong>Tipo:</strong> {renderBadgeTipo(comissaoSelecionada.tipo_comissao)}</div>
            </div>

            {comissaoSelecionada.tipo_comissao === 'socios' ? (
              <div>
                <h4 className="font-medium mb-3 text-sm">Cenários de Comissão por Sócios:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: '👥 Ambos Sócios', c: comissaoSelecionada.ambos_socios_comprador, v: comissaoSelecionada.ambos_socios_vendedor, d: comissaoSelecionada.ambos_socios_defesa },
                    { label: '🛒 Só Comprador Sócio', c: comissaoSelecionada.comprador_socio_comprador, v: comissaoSelecionada.comprador_socio_vendedor, d: comissaoSelecionada.comprador_socio_defesa },
                    { label: '🏪 Só Vendedor Sócio', c: comissaoSelecionada.vendedor_socio_comprador, v: comissaoSelecionada.vendedor_socio_vendedor, d: comissaoSelecionada.vendedor_socio_defesa },
                    { label: '👤 Nenhum Sócio', c: comissaoSelecionada.comissao_compra, v: comissaoSelecionada.comissao_venda, d: comissaoSelecionada.comissao_defesa },
                  ].map(({ label, c, v, d }) => (
                    <div key={label} className="border p-3 rounded text-sm">
                      <strong className="block mb-1">{label}</strong>
                      <span className="text-gray-600">C: {formatarPercentual(c)} · V: {formatarPercentual(v)} · D: {formatarPercentual(d)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : comissaoSelecionada.tipo_comissao === 'por_qtd' ? (
              <div className="text-sm space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><strong>Comprador:</strong><br/>{formatarPercentual(comissaoSelecionada.comissao_compra)}</div>
                  <div><strong>Defesa:</strong><br/>{formatarPercentual(comissaoSelecionada.comissao_defesa)}</div>
                </div>
                <div>
                  <strong className="block mb-1">Comissão de venda por quantidade de animais:</strong>
                  <div className="border rounded divide-y">
                    {(comissaoSelecionada.faixas_qtd_venda || []).map((f, i) => (
                      <div key={i} className="flex justify-between px-3 py-2">
                        <span>{textoFaixa(f)}</span>
                        <span className="font-medium">{formatarPercentual(f.percentual)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:gap-4 text-sm">
                <div><strong>Comprador:</strong><br/>{formatarPercentual(comissaoSelecionada.comissao_compra)}</div>
                <div><strong>Vendedor:</strong><br/>{formatarPercentual(comissaoSelecionada.comissao_venda)}</div>
                <div><strong>Defesa:</strong><br/>{formatarPercentual(comissaoSelecionada.comissao_defesa)}</div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button onClick={() => setShowDetalhesModal(false)} className="btn-secondary">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showConfirmDelete && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                Excluir Comissão
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir a comissão "{itemToDelete.nome}"? Esta ação não pode ser desfeita.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmDelete(false);
                  setItemToDelete(null);
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarExclusao}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ComissoesLista;